import apiClient from './api.service';
import {
  MyCommissionDashboard,
  MyCommission,
  CommissionPayment,
  PotentialEarnings
} from '../types/commission.types';

/**
 * My Commissions Service
 * Handles API calls for personal commission dashboard
 */
class MyCommissionsService {
  private hasAnyInflight = new Map<number, Promise<boolean>>();

  /**
   * Check if current user has at least one commission in this store (lightweight, for tab visibility).
   */
  async getHasAnyCommission(storeId: number): Promise<boolean> {
    const existing = this.hasAnyInflight.get(storeId);
    if (existing) return existing;
    const request = apiClient.get(`/stores/${storeId}/commissions/my-has-any`)
      .then((response) => response.data.data?.hasAny === true)
      .finally(() => {
        this.hasAnyInflight.delete(storeId);
      });
    this.hasAnyInflight.set(storeId, request);
    return request;
  }

  /**
   * Get personal dashboard summary
   */
  async getDashboard(
    storeId: number,
    periodStart?: string,
    periodEnd?: string
  ): Promise<MyCommissionDashboard> {
    const params: any = {};
    if (periodStart) params.period_start = periodStart;
    if (periodEnd) params.period_end = periodEnd;

    const response = await apiClient.get(`/stores/${storeId}/commissions/my-dashboard`, { params });
    return response.data.data;
  }

  /**
   * Get detailed commissions list
   */
  async getMyCommissions(
    storeId: number,
    periodStart?: string,
    periodEnd?: string,
    includePaid: boolean = true
  ): Promise<{ commissions: MyCommission[]; period: { start: string; end: string } }> {
    const params: any = {
      include_paid: includePaid.toString()
    };
    if (periodStart) params.period_start = periodStart;
    if (periodEnd) params.period_end = periodEnd;

    const response = await apiClient.get(`/stores/${storeId}/commissions/my-commissions`, { params });
    return response.data.data;
  }

  /**
   * Get payments history
   */
  async getMyPayments(
    storeId: number,
    periodStart?: string,
    periodEnd?: string
  ): Promise<{ payments: CommissionPayment[]; period: { start: string; end: string } }> {
    const params: any = {};
    if (periodStart) params.period_start = periodStart;
    if (periodEnd) params.period_end = periodEnd;

    const response = await apiClient.get(`/stores/${storeId}/commissions/my-payments`, { params });
    return response.data.data;
  }

  /**
   * Get potential earnings
   */
  async getPotentialEarnings(storeId: number): Promise<PotentialEarnings> {
    const response = await apiClient.get(`/stores/${storeId}/commissions/my-potential`);
    return response.data.data;
  }
}

export default new MyCommissionsService();
